import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { optionalAuthHeaders } from '../utils/billingApi';
import './BrokerageChecklist.css';
import { getContractApiUrl, getTmsApiUrl } from '../utils/apiUrls';

const BACKEND_URL = getContractApiUrl();
const TMS_URL = getTmsApiUrl();

// Roles on the transaction team that are allowed to see the brokerage checklist.
// The DB stores both value-format ('buyer_agent') and label-format ("Buyer's Agent")
// depending on how the member was added, so we check both.
const ALLOWED_TEAM_ROLES = new Set([
  'buyer_agent', "Buyer's Agent",
  'sellers_agent', "Seller's Agent", 'Listing Agent',
  'buyers_transaction_coordinator', "Buyer's Transaction Coordinator",
  'sellers_transaction_coordinator', "Seller's Transaction Coordinator",
]);

// Dev/test accounts that can always see the checklist regardless of team role,
// for non-production testing purposes.
const DEV_WHITELIST = new Set([
  'flo-test1@keyflo.ai',
  'cole@keyflo.ai',
  'james@keyflo.ai',
]);

const BrokerageChecklist = ({ contractId, isExpanded, onToggle }) => {
  const { user, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [checklist, setChecklist] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  // null = still loading; true/false = resolved
  const [canView, setCanView] = useState(null);

  useEffect(() => {
    if (contractId && user?.email) {
      fetchChecklist();
    }
  }, [contractId, user?.email]);

  const fetchChecklist = async () => {
    const headers = await optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
    const userEmail = encodeURIComponent(user.email);
    const [checklistResult, syncResult, profileResult, teamResult] = await Promise.allSettled([
      axios.get(`${TMS_URL}/checklist/${contractId}?user_email=${userEmail}`, { headers }),
      axios.get(`${TMS_URL}/sync/${contractId}?user_email=${userEmail}`, { headers }),
      axios.get(`${BACKEND_URL}/user/profile?email=${userEmail}`),
      axios.get(`${BACKEND_URL}/contracts/${contractId}/team`),
    ]);

    if (checklistResult.status === 'fulfilled') {
      setChecklist(checklistResult.value.data);
    } else if (checklistResult.reason?.response?.status === 404) {
      setChecklist({ tms_configured: false });
    } else if (checklistResult.reason) {
      console.error('Error fetching brokerage checklist:', checklistResult.reason);
    }

    if (syncResult.status === 'fulfilled') {
      setSyncStatus(syncResult.value.data);
    } else if (syncResult.reason) {
      console.error('Error fetching TMS sync status:', syncResult.reason);
    }

    const profile = profileResult.status === 'fulfilled' ? profileResult.value.data : null;
    const userType = profile?.user_type ?? null;
    const isSuperAdmin = Boolean(profile?.is_super_admin);
    const team = teamResult.status === 'fulfilled' && Array.isArray(teamResult.value.data)
      ? teamResult.value.data
      : [];
    const myEntries = team.filter(m => m.email?.toLowerCase() === user.email.toLowerCase());
    const isManagingBroker = userType === 'managing_broker';
    const hasAllowedRole = myEntries.some(m => ALLOWED_TEAM_ROLES.has(m.role));
    const isDevUser = DEV_WHITELIST.has((user.email || '').toLowerCase());
    const hasOwnTmsSync = syncResult.status === 'fulfilled'
      && syncResult.value.data?.external_id
      && syncResult.value.data?.sync_status !== 'not_started';
    setCanView(
      isDevUser
      || isSuperAdmin
      || (hasAllowedRole && hasOwnTmsSync)
      || (isManagingBroker && hasOwnTmsSync),
    );
  };

  if (canView === null) return null;
  if (!canView) return null;

  if (checklist && checklist.tms_configured === false) return null;
  const externalId = syncStatus?.external_id || checklist?.external_transaction_id;
  if (checklist && checklist.tms_configured && !externalId) return null;

  const needed = checklist?.needed || [];
  const onFile = checklist?.on_file || [];

  const requiredNeeded = needed.filter(item => item.is_required);
  const optionalNeeded = needed.filter(item => !item.is_required);
  const requiredOnFile = onFile.filter(item => item.is_required);
  const requiredTotal = requiredNeeded.length + requiredOnFile.length;

  const syncStatusLabel = () => {
    if (!syncStatus) return null;
    const s = syncStatus.sync_status;
    if (s === 'synced') return { text: '● Synced', cls: 'tms-status-synced' };
    if (s === 'error') return { text: '● Sync Error', cls: 'tms-status-error' };
    if (s === 'pending') return { text: '◌ Pending', cls: 'tms-status-pending' };
    return null;
  };
  const statusLabel = syncStatusLabel();

  return (
    <div className="brokerage-checklist-section">
      <div className="bc-header" onClick={onToggle}>
        <div className="bc-header-left">
          <h2 className="bc-header-title">Brokerage Document Checklist</h2>
          {statusLabel && (
            <span className={`tms-sync-status ${statusLabel.cls}`}>{statusLabel.text}</span>
          )}
        </div>
        <div className="bc-header-right">
          {requiredTotal > 0 && (
            <div className="bc-progress">
              <div className="bc-progress-bar">
                <div
                  className="bc-progress-fill"
                  style={{ width: `${Math.round((requiredOnFile.length / requiredTotal) * 100)}%` }}
                />
              </div>
              <span className="bc-progress-label">
                {requiredOnFile.length}/{requiredTotal}
              </span>
            </div>
          )}
          <button
            className="bc-chevron"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg
              width="20" height="20" viewBox="0 0 24 24"
              fill="none" stroke="currentColor"
              strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="bc-body">
          {!checklist ? (
            <p className="bc-loading">Loading checklist...</p>
          ) : (
            <>
              {requiredNeeded.length > 0 && (
                <ChecklistGroup
                  label="Required"
                  count={requiredNeeded.length}
                  items={requiredNeeded}
                  variant="needed"
                  icon="○"
                />
              )}

              {optionalNeeded.length > 0 && (
                <ChecklistGroup
                  label="Optional"
                  count={optionalNeeded.length}
                  items={optionalNeeded}
                  variant="optional"
                  icon="○"
                />
              )}

              {needed.length === 0 && (
                <p className="bc-all-clear">All required documents are on file.</p>
              )}

              <ChecklistGroup
                label="On File"
                count={onFile.length}
                items={onFile}
                variant="on-file"
                icon="✓"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ChecklistGroup = ({ label, count, items, variant, icon }) => (
  <div className={`bc-group bc-group--${variant}`}>
    <div className="bc-group-header">
      <span className={`bc-group-label bc-group-label--${variant}`}>{label}</span>
      <span className="bc-group-count">{count} {count === 1 ? 'doc' : 'docs'}</span>
    </div>
    {items.length === 0 ? (
      <p className="bc-group-empty">None yet</p>
    ) : (
      <div className="bc-items">
        {items.map((item, i) => (
          <div key={i} className={`bc-item bc-item--${variant}`}>
            <span className="bc-item-icon">{icon}</span>
            <span className="bc-item-name">{item.item_name}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default BrokerageChecklist;
